import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import Tour, { ReactourStep, ReactourStepPosition } from 'reactour';

import { usePets } from '../../../context/PetsContext';
import { useTutorial } from '../../../context/TutorialContext';
import { useDynamicTheme } from '../../../theme/useDynamicTheme';
import { isDawerPet } from '../../../utils/coverage';
import Step from '../Step';
import './Tutorial.scss';
import { TutorialProps } from './types';

const DAWER_TUTORIAL_ACCENT = '#176fbd';
const ADD_PET_SELECTOR = '[data-tut="reactour__add_pet_button"]';
const PETS_LIST_SELECTOR = '[data-tut="reactour__pets_list"]';
const PET_INFO_SELECTOR = '[data-tut="reactour__pet_info"]';
const PET_SINIESTRO_SELECTOR = '[data-tut="reactour__pet_siniestro"]';
const DAWER_PET_SUMMARY_SELECTOR = '[data-tut="reactour__dawer_pet_summary"]';
const DAWER_COVERAGE_SELECTOR = '[data-tut="reactour__dawer_coverage"]';
const DAWER_PLAN_SELECTOR = '[data-tut="reactour__dawer_plan"]';
const PET_TAB_SELECTOR = '[data-tut="reactour__pet_icon"]';
const USER_TAB_SELECTOR = '[data-tut="reactour__user_icon"]';

const topPosition = 'top' as ReactourStepPosition;
const bottomPosition = 'bottom' as ReactourStepPosition;
const centerPosition = 'center' as ReactourStepPosition;
const bottomHelperStyle = { marginTop: 18 };
const topHelperStyle = { marginTop: -18 };

type IonicContentElement = HTMLElement & {
  getScrollElement?: () => Promise<HTMLElement>;
};

const getHomeScrollElement = async (): Promise<HTMLElement | null> => {
  const ionContent = document.querySelector(
    'ion-content.home-page__content',
  ) as IonicContentElement | null;

  if (ionContent?.getScrollElement) {
    return ionContent.getScrollElement();
  }

  return document.scrollingElement as HTMLElement | null;
};

const getFooterClearance = (): number => {
  const tabBar = document.querySelector('ion-tab-bar') as HTMLElement | null;
  const tabBarHeight = tabBar?.getBoundingClientRect().height ?? 76;

  return tabBarHeight + 18;
};

const ensureHomeTargetIsFullyVisible = async (
  target: Element | null,
  options: { preferCenter?: boolean } = {},
): Promise<boolean> => {
  if (!target) return false;

  const scrollElement = await getHomeScrollElement();
  if (!scrollElement) return false;

  const targetRect = target.getBoundingClientRect();
  const scrollRect = scrollElement.getBoundingClientRect();
  const topLimit = Math.max(scrollRect.top, 16);
  const bottomLimit = Math.min(
    scrollRect.bottom,
    window.innerHeight - getFooterClearance(),
  );
  const availableHeight = bottomLimit - topLimit;
  let delta = 0;

  if (options.preferCenter && targetRect.height < availableHeight) {
    const centeredDelta =
      targetRect.top - topLimit - (availableHeight - targetRect.height) / 2;

    delta = centeredDelta;
  } else if (targetRect.height >= availableHeight) {
    delta = targetRect.top - topLimit;
  } else if (targetRect.bottom > bottomLimit) {
    delta = targetRect.bottom - bottomLimit;

    const nextTop = targetRect.top - delta;
    if (nextTop < topLimit) {
      delta -= topLimit - nextTop;
    }
  } else if (targetRect.top < topLimit) {
    delta = targetRect.top - topLimit;
  }

  if (Math.abs(delta) < 2) return false;

  scrollElement.scrollTo({
    top: Math.max(0, scrollElement.scrollTop + delta),
    behavior: 'auto',
  });

  return true;
};

const resetHomeScrollPosition = async (): Promise<boolean> => {
  const scrollElement = await getHomeScrollElement();
  if (!scrollElement || scrollElement.scrollTop < 2) return false;

  scrollElement.scrollTo({ top: 0, behavior: 'auto' });
  return true;
};

const Tutorial: React.FC<TutorialProps> = () => {
  const location = useLocation();
  const { theme } = useDynamicTheme();
  const { currentPet, pets, isValidating: isPetsValidating } = usePets();
  const {
    setTutorialDone,
    startTour,
    setStartTour,
    goToStep,
    setGoToStep,
  } = useTutorial();

  const [steps, setSteps] = useState<ReactourStep[]>([]);
  const [tourUpdateKey, setTourUpdateKey] = useState('');
  const isClosingTourRef = useRef(false);
  const isHomeRoute = location.pathname.startsWith('/tabs/home');
  const petList = useMemo(() => (Array.isArray(pets) ? pets : []), [pets]);
  const hasOnlyDawerPets =
    petList.length > 0 && petList.every((pet) => isDawerPet(pet));
  const isDawer = isDawerPet(currentPet);
  const shouldUseDawerTutorial = hasOnlyDawerPets || isDawer;
  const brandName = theme?.brandName;
  const accentColor = shouldUseDawerTutorial
    ? DAWER_TUTORIAL_ACCENT
    : theme?.colors.primary;

  const closeTutorial = useCallback(() => {
    if (isClosingTourRef.current) return;

    isClosingTourRef.current = true;
    setStartTour(false);
    setTutorialDone(true);
  }, [setStartTour, setTutorialDone]);

  useEffect(() => {
    if (startTour) {
      isClosingTourRef.current = false;
    }
  }, [startTour]);

  const handleCurrentStepChange = useCallback(
    (step: number) => {
      setGoToStep(step);
    },
    [setGoToStep],
  );

  const tutorialSteps = useMemo(() => {
    const introStep: ReactourStep = {
      content: (
        <Step
          title={`Bienvenido a ${brandName}`}
          body="En este breve tutorial te mostramos cómo gestionar tus mascotas y acceder a todas las funcionalidades de la app."
        />
      ),
      position: centerPosition,
    };

    const addPetStep: ReactourStep = {
      selector: ADD_PET_SELECTOR,
      content: (
        <Step
          title="Agregar una nueva mascota"
          body="Desde acá podés asegurar una nueva mascota y completar sus datos para comenzar a usar todos los servicios."
        />
      ),
      position: bottomPosition,
      style: bottomHelperStyle,
      stepInteraction: false,
    };

    const petsListStep: ReactourStep = {
      selector: PETS_LIST_SELECTOR,
      content: (
        <Step
          title="Lista de mascotas"
          body="Acá vas a ver todas tus mascotas. Tocá cualquiera para ingresar a su perfil y gestionar sus servicios."
        />
      ),
      position: bottomPosition,
      style: bottomHelperStyle,
      stepInteraction: false,
    };

    const petTabStep: ReactourStep = {
      selector: PET_TAB_SELECTOR,
      content: (
        <Step
          title="Menú de la mascota"
          body="Desde este menú podés gestionar todo lo relacionado a tu mascota: servicios, historial y opciones."
        />
      ),
      position: topPosition,
      style: topHelperStyle,
      stepInteraction: false,
    };

    const userTabStep: ReactourStep = {
      selector: USER_TAB_SELECTOR,
      content: (
        <Step
          title="Tu perfil"
          body="Desde acá podés ver y editar tus datos personales, información de la cuenta y servicios adicionales."
        />
      ),
      position: topPosition,
      style: topHelperStyle,
      stepInteraction: false,
    };

    const forceTourRecalculation = () => {
      window.setTimeout(() => {
        setTourUpdateKey(`${Date.now()}`);
      }, 40);
    };
    const keepDawerTargetVisible: ReactourStep['action'] = (node) => {
      void (async () => {
        const moved = await ensureHomeTargetIsFullyVisible(node);

        if (moved) {
          forceTourRecalculation();
        }
      })();
    };
    const centerDawerTargetVisible: ReactourStep['action'] = (node) => {
      void (async () => {
        const moved = await ensureHomeTargetIsFullyVisible(node, {
          preferCenter: true,
        });

        if (moved) {
          forceTourRecalculation();
        }
      })();
    };
    const resetHomeBeforeFooterStep: ReactourStep['action'] = () => {
      void (async () => {
        const moved = await resetHomeScrollPosition();

        if (moved) {
          forceTourRecalculation();
        }
      })();
    };
    const petFooterStep: ReactourStep = {
      ...petTabStep,
      action: resetHomeBeforeFooterStep,
    };
    const userFooterStep: ReactourStep = {
      ...userTabStep,
      action: resetHomeBeforeFooterStep,
    };
    const doneStep: ReactourStep = {
      content: (
        <Step
          title="¡Todo listo!"
          body="Ya conocés las secciones principales. Podés empezar a usar la app y explorar cada funcionalidad cuando quieras."
        />
      ),
      position: centerPosition,
      action: resetHomeBeforeFooterStep,
    };

    const bbvaSteps: ReactourStep[] = [
      introStep,
      addPetStep,
      petsListStep,
      {
        selector: PET_INFO_SELECTOR,
        content: (
          <Step
            title="Perfil de la mascota"
            body="En el perfil podés consultar los datos principales, coberturas y servicios disponibles de tu mascota."
          />
        ),
        position: bottomPosition,
        style: bottomHelperStyle,
        stepInteraction: false,
      },
      {
        selector: PET_SINIESTRO_SELECTOR,
        content: (
          <Step
            title="Casos y cuidados"
            body="Desde acá podés registrar accidentes, enfermedades o vacunas y hacer seguimiento de cada caso."
          />
        ),
        position: topPosition,
        style: topHelperStyle,
        stepInteraction: false,
      },
      petFooterStep,
      userFooterStep,
      doneStep,
    ];

    const dawerSteps: ReactourStep[] = [
      introStep,
      addPetStep,
      petsListStep,
      {
        selector: DAWER_PLAN_SELECTOR,
        content: (
          <Step
            title="Contratar cobertura"
            body="Desde acá podés elegir un plan y cargar tus datos de pago para mantener activa la cobertura de tu mascota."
          />
        ),
        position: bottomPosition,
        style: bottomHelperStyle,
        action: keepDawerTargetVisible,
        stepInteraction: false,
      },
      {
        selector: DAWER_PET_SUMMARY_SELECTOR,
        content: (
          <Step
            title="Perfil de la mascota"
            body="En esta ficha podés consultar los datos principales de la mascota seleccionada y acceder a Telemedicina."
          />
        ),
        position: bottomPosition,
        style: bottomHelperStyle,
        action: keepDawerTargetVisible,
        stepInteraction: false,
      },
      {
        selector: DAWER_COVERAGE_SELECTOR,
        content: (
          <Step
            title="Progreso de cobertura"
            body="Esta sección muestra cuántos días quedan de cobertura gratuita y cuánto avanzó el período inicial."
          />
        ),
        position: topPosition,
        style: topHelperStyle,
        action: centerDawerTargetVisible,
        stepInteraction: false,
      },
      petFooterStep,
      userFooterStep,
      doneStep,
    ];

    return {
      bbva: bbvaSteps,
      dawer: dawerSteps,
    };
  }, [brandName]);

  useEffect(() => {
    if (isClosingTourRef.current) return;

    if (!isHomeRoute || isPetsValidating || !startTour) {
      setSteps([]);
      return;
    }

    setSteps(shouldUseDawerTutorial ? tutorialSteps.dawer : tutorialSteps.bbva);
    setGoToStep(0);
  }, [
    isPetsValidating,
    isHomeRoute,
    shouldUseDawerTutorial,
    startTour,
    tutorialSteps,
    setGoToStep,
  ]);

  if (!isHomeRoute || !steps.length) {
    return null;
  }

  return (
    <Tour
      isOpen={startTour}
      onBeforeClose={closeTutorial}
      onRequestClose={closeTutorial}
      steps={steps}
      className="app-tutorial"
      goToStep={goToStep}
      getCurrentStep={handleCurrentStepChange}
      startAt={0}
      accentColor={accentColor}
      update={tourUpdateKey}
      updateDelay={40}
      maskSpace={8}
      rounded={5}
    />
  );
};

export default Tutorial;
